import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, serverTimestamp, setDoc, writeBatch, collection } from 'firebase/firestore';
import { functions, db } from '../../firebase';
import { useParty } from '../../contexts/PartyContext';
import { useSubscription } from '../../hooks/useSubscription';
import TestTonePanel from '../audio/TestTonePanel';
import AudioLevelMeter from '../audio/AudioLevelMeter';
import { endPartyClient } from '../../services/partyLifecycle';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	partyId?: string;
	initialTab?: 'audio' | 'sources' | 'video' | 'queue';
	splashCountdown?: number;
	onSplashCountdownChange?: (v: number) => void;
	splashPaused?: boolean;
	onSplashPausedChange?: (v: boolean) => void;
  // Optional: show a setup tip banner when first opened
  showSetupTip?: boolean;
  onDismissSetupTip?: () => void;
  // Optional: report mic status when closing the modal
  onCloseWithMicStatus?: (anyMicLive: boolean) => void;
};

const HostControlsModalRegular: React.FC<Props> = ({
	isOpen,
	onClose,
	partyId,
		initialTab,
	splashCountdown: propSplashCountdown,
	onSplashCountdownChange,
	splashPaused: propSplashPaused,
	onSplashPausedChange,
  showSetupTip,
  onDismissSetupTip,
  onCloseWithMicStatus,
}) => {
	const { songQueue, currentParty } = useParty();
	const subscription = useSubscription();

	const resolvedPartyId = currentParty?.id ?? partyId ?? '';

		const [activeTab, setActiveTab] = useState<'audio' | 'sources' | 'video' | 'queue'>(initialTab || 'audio');
	const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
	const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
	const [deviceSelection, setDeviceSelection] = useState<string>('');

	// Dual local USB mics (host device)
	const [selectedMic1, setSelectedMic1] = useState<string>('');
	const [selectedMic2, setSelectedMic2] = useState<string>('');
	const [mic1Live, setMic1Live] = useState<boolean>(false);
	const [mic2Live, setMic2Live] = useState<boolean>(false);
	const [micStream1, setMicStream1] = useState<MediaStream | null>(null);
	const [micStream2, setMicStream2] = useState<MediaStream | null>(null);
	const [micVol1, setMicVol1] = useState<number>(() => {
		try { return Math.max(0, Math.min(100, Number(localStorage.getItem('qroke:mic1Volume') || '80'))); } catch { return 80; }
	});
	const [micVol2, setMicVol2] = useState<number>(() => {
		try { return Math.max(0, Math.min(100, Number(localStorage.getItem('qroke:mic2Volume') || '80'))); } catch { return 80; }
	});
	const [isStartingMic1, setIsStartingMic1] = useState(false);
	const [isStartingMic2, setIsStartingMic2] = useState(false);
			// Effects state (simple, low‑latency): Reverb, Echo, EQ sliders, Pan, Gate, Pitch (Key)
			// Defaults to 0 for minimal latency
			type ReverbType = 'room' | 'hall';
			type GateLevel = 'off' | 'low' | 'med' | 'high';
			const [reverb1, setReverb1] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic1Reverb') || '0'); } catch { return 0; } });
			const [reverbType1, setReverbType1] = useState<ReverbType>(() => { try { return (localStorage.getItem('qroke:mic1ReverbType') as ReverbType) || 'room'; } catch { return 'room'; } });
			const [echo1, setEcho1] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic1Echo') || '0'); } catch { return 0; } });
			const [pan1, setPan1] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic1Pan') || '0'); } catch { return 0; } });
			const [low1, setLow1] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic1Low') || '0'); } catch { return 0; } });
			const [mid1, setMid1] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic1Mid') || '0'); } catch { return 0; } });
			const [high1, setHigh1] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic1High') || '0'); } catch { return 0; } });
			const [gate1, setGate1] = useState<GateLevel>(() => { try { return (localStorage.getItem('qroke:mic1Gate') as GateLevel) || 'off'; } catch { return 'off'; } });
			const [keyShift1, setKeyShift1] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic1Key') || '0'); } catch { return 0; } });

			const [reverb2, setReverb2] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic2Reverb') || '0'); } catch { return 0; } });
			const [reverbType2, setReverbType2] = useState<ReverbType>(() => { try { return (localStorage.getItem('qroke:mic2ReverbType') as ReverbType) || 'room'; } catch { return 'room'; } });
			const [echo2, setEcho2] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic2Echo') || '0'); } catch { return 0; } });
			const [pan2, setPan2] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic2Pan') || '0'); } catch { return 0; } });
			const [low2, setLow2] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic2Low') || '0'); } catch { return 0; } });
			const [mid2, setMid2] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic2Mid') || '0'); } catch { return 0; } });
			const [high2, setHigh2] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic2High') || '0'); } catch { return 0; } });
			const [gate2, setGate2] = useState<GateLevel>(() => { try { return (localStorage.getItem('qroke:mic2Gate') as GateLevel) || 'off'; } catch { return 'off'; } });
			const [keyShift2, setKeyShift2] = useState<number>(() => { try { return Number(localStorage.getItem('qroke:mic2Key') || '0'); } catch { return 0; } });

		// Global hidden audio sinks in document.body so mic audio continues after modal closes
		const getMicSinkEl = (index: 1 | 2): HTMLAudioElement => {
			const id = index === 1 ? 'qroke-host-mic-1' : 'qroke-host-mic-2';
			let el = document.getElementById(id) as HTMLAudioElement | null;
				if (!el) {
					el = document.createElement('audio');
					el.id = id;
					el.autoplay = true;
					el.hidden = true;
					document.body.appendChild(el);
				}
			// Keep mic sinks at unity; mix via Web Audio graph
			try { el.volume = 1.0; } catch {}
			return el;
		};

		// Single shared AudioContext for effect graphs
			const audioCtxRef = useRef<AudioContext | null>(null);
			const impulseRoomRef = useRef<AudioBuffer | null>(null);
			const impulseHallRef = useRef<AudioBuffer | null>(null);
		type MicGraph = {
				source: MediaStreamAudioSourceNode;
				pitchNode?: AudioWorkletNode | null;
			dryGain: GainNode;
			masterGain: GainNode;
			reverb: ConvolverNode;
			reverbGain: GainNode;
			delay: DelayNode;
			feedback: GainNode;
			echoGain: GainNode;
			eqLow: BiquadFilterNode;
			eqMid: BiquadFilterNode;
			eqHigh: BiquadFilterNode;
			compressor: DynamicsCompressorNode;
				gateGain: GainNode;
				analyser: AnalyserNode;
				gateLoopId: number | null;
				panner: StereoPannerNode | null;
			dest: MediaStreamAudioDestinationNode;
		};
		const micGraph1Ref = useRef<MicGraph | null>(null);
		const micGraph2Ref = useRef<MicGraph | null>(null);

	const [endingParty, setEndingParty] = useState(false);
	const [processingQueue, setProcessingQueue] = useState(false);
	const [isScanning, setIsScanning] = useState(false);
	const [internalMasterVolume, setInternalMasterVolume] = useState<number>(80);
	const [extVolume, setExtVolume] = useState<number>(100);
	const [extMuted, setExtMuted] = useState<boolean>(false);
	const [externalDisplayUrl, setExternalDisplayUrl] = useState<string>('');
	const [venueAdScheduled, setVenueAdScheduled] = useState<{ status: 'scheduled' | 'playing' | 'played' | 'cancelled'; durationSeconds?: number } | null>(null);
		// Low-latency mode: route mics directly to AudioContext destination (system default output) and disable browser DSP
		// Default to ON when no preference is stored
		const [lowLatencyMode, setLowLatencyMode] = useState<boolean>(() => {
			try {
				const v = localStorage.getItem('qroke:lowLatency');
				return v === null ? true : v === '1';
			} catch {
				return true;
			}
		});

	// UI: help popover for mic options
	const [showMicHelp, setShowMicHelp] = useState<boolean>(false);

		// Track source state (legacy; UI moved to Sources tab but keeping state for compatibility)
		const [trackSource, setTrackSource] = useState<'youtube' | 'local'>(() => {
			try { return (localStorage.getItem('qroke:trackSource') as any) || 'youtube'; } catch { return 'youtube'; }
		});

		// Local Library connector state
		const [folderHandle, setFolderHandle] = useState<FileSystemDirectoryHandle | null>(null);
			type LibraryItem = { id: string; title: string; artist?: string | null; durationSeconds?: number | null; path?: string; kind: 'audio'|'video'|'cdg'; mp3Path?: string; cdgPath?: string };
			const [libraryIndex, setLibraryIndex] = useState<LibraryItem[]>([]);
		const [libFilter, setLibFilter] = useState('');
		const [isIndexing, setIsIndexing] = useState(false);
		const [publishStatus, setPublishStatus] = useState<string | null>(null);

	// Splash controls: use props if provided, else local state
	const [localSplashCountdown, setLocalSplashCountdown] = useState<number>(10);
	const [localSplashPaused, setLocalSplashPaused] = useState<boolean>(false);
	const splashCountdown = typeof propSplashCountdown === 'number' ? propSplashCountdown : localSplashCountdown;
	const splashPaused = typeof propSplashPaused === 'boolean' ? propSplashPaused : localSplashPaused;

	useEffect(() => {
		if (!isOpen) return;
		// When opening, apply initialTab if provided
		if (initialTab) {
			setActiveTab(initialTab);
		}
		void refreshDevices();
		// External display URL and persisted external volume/mute
		if (resolvedPartyId) {
			setExternalDisplayUrl(`https://qroke-a6fed.firebaseapp.com/party/${resolvedPartyId}?display=1`);
			try {
				const volKey = `qroke:ytv:${resolvedPartyId}:volume`;
				const muteKey = `qroke:ytv:${resolvedPartyId}:muted`;
				const v = localStorage.getItem(volKey);
				const m = localStorage.getItem(muteKey);
				if (v !== null) setExtVolume(Math.max(0, Math.min(100, Number(v))));
				if (m !== null) setExtMuted(m === '1');
			} catch {}
		} else {
			setExternalDisplayUrl('');
		}
		// Subscribe to scheduled venue ad doc to reflect UI state
		if (resolvedPartyId) {
			const ref = doc(db, 'parties', resolvedPartyId, 'control', 'scheduledAd');
			const unsub = onSnapshot(ref, (snap) => {
				if (snap.exists()) {
					const d = snap.data() as any;
					setVenueAdScheduled({ status: d.status ?? 'scheduled', durationSeconds: d.durationSeconds });
				} else {
					setVenueAdScheduled(null);
				}
			});
			return () => unsub();
		}
	}, [isOpen, resolvedPartyId]);

	// Apply mic volumes via graph masterGain; keep sinks at unity
	useEffect(() => {
		try { localStorage.setItem('qroke:mic1Volume', String(micVol1)); } catch {}
		const g = micGraph1Ref.current; if (g && g.masterGain) {
			try { g.masterGain.gain.value = (internalMasterVolume / 100) * (micVol1 / 100); } catch {}
		}
		const el = getMicSinkEl(1); if (el) { try { el.volume = 1.0; } catch {} }
	}, [micVol1, internalMasterVolume]);
	useEffect(() => {
		try { localStorage.setItem('qroke:mic2Volume', String(micVol2)); } catch {}
		const g = micGraph2Ref.current; if (g && g.masterGain) {
			try { g.masterGain.gain.value = (internalMasterVolume / 100) * (micVol2 / 100); } catch {}
		}
		const el = getMicSinkEl(2); if (el) { try { el.volume = 1.0; } catch {} }
	}, [micVol2, internalMasterVolume]);

			// Persist effects
			useEffect(() => { try { localStorage.setItem('qroke:mic1Reverb', String(reverb1)); } catch {} }, [reverb1]);
			useEffect(() => { try { localStorage.setItem('qroke:mic1ReverbType', reverbType1); } catch {} }, [reverbType1]);
			useEffect(() => { try { localStorage.setItem('qroke:mic1Echo', String(echo1)); } catch {} }, [echo1]);
			useEffect(() => { try { localStorage.setItem('qroke:mic1Pan', String(pan1)); } catch {} }, [pan1]);
			useEffect(() => { try { localStorage.setItem('qroke:mic1Low', String(low1)); } catch {} }, [low1]);
			useEffect(() => { try { localStorage.setItem('qroke:mic1Mid', String(mid1)); } catch {} }, [mid1]);
			useEffect(() => { try { localStorage.setItem('qroke:mic1High', String(high1)); } catch {} }, [high1]);
			useEffect(() => { try { localStorage.setItem('qroke:mic1Gate', gate1); } catch {} }, [gate1]);
			useEffect(() => { try { localStorage.setItem('qroke:mic1Key', String(keyShift1)); } catch {} }, [keyShift1]);

			useEffect(() => { try { localStorage.setItem('qroke:mic2Reverb', String(reverb2)); } catch {} }, [reverb2]);
			useEffect(() => { try { localStorage.setItem('qroke:mic2ReverbType', reverbType2); } catch {} }, [reverbType2]);
			useEffect(() => { try { localStorage.setItem('qroke:mic2Echo', String(echo2)); } catch {} }, [echo2]);
			useEffect(() => { try { localStorage.setItem('qroke:mic2Pan', String(pan2)); } catch {} }, [pan2]);
			useEffect(() => { try { localStorage.setItem('qroke:mic2Low', String(low2)); } catch {} }, [low2]);
			useEffect(() => { try { localStorage.setItem('qroke:mic2Mid', String(mid2)); } catch {} }, [mid2]);
			useEffect(() => { try { localStorage.setItem('qroke:mic2High', String(high2)); } catch {} }, [high2]);
			useEffect(() => { try { localStorage.setItem('qroke:mic2Gate', gate2); } catch {} }, [gate2]);
			useEffect(() => { try { localStorage.setItem('qroke:mic2Key', String(keyShift2)); } catch {} }, [keyShift2]);

			const ensureAudioCtx = () => {
				if (!audioCtxRef.current) {
					const Ctor: any = (window as any).AudioContext || (window as any).webkitAudioContext;
					try {
						audioCtxRef.current = new Ctor({ latencyHint: 'interactive' });
					} catch {
						audioCtxRef.current = new Ctor();
					}
				}
				return audioCtxRef.current;
			};

		// Simple synthetic impulse response for a small room reverb
			const ensureImpulse = async (ctx: AudioContext, type: ReverbType) => {
				const cache = type === 'hall' ? impulseHallRef : impulseRoomRef;
				if (cache.current) return cache.current;
				const seconds = type === 'hall' ? 3.2 : 1.6;
				const decay = type === 'hall' ? 3.5 : 2.2;
				const rate = ctx.sampleRate; const length = Math.floor(rate * seconds);
				const impulse = ctx.createBuffer(2, length, rate);
			for (let ch = 0; ch < 2; ch++) {
				const channelData = impulse.getChannelData(ch);
				for (let i = 0; i < length; i++) {
					channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
				}
			}
				if (type === 'hall') impulseHallRef.current = impulse; else impulseRoomRef.current = impulse;
				return impulse;
		};

			const applyEqGains = (graph: MicGraph, lowDb: number, midDb: number, highDb: number) => {
				graph.eqLow.gain.value = Math.max(-6, Math.min(6, lowDb));
				graph.eqMid.gain.value = Math.max(-6, Math.min(6, midDb));
				graph.eqHigh.gain.value = Math.max(-6, Math.min(6, highDb));
			};

			const gateThresholdValue = (lvl: GateLevel) => {
				switch (lvl) {
					case 'low': return 0.03;
					case 'med': return 0.06;
					case 'high': return 0.1;
					default: return 0;
				}
			};

			const updateGateLoop = (graph: MicGraph, level: GateLevel) => {
				if (graph.gateLoopId) { cancelAnimationFrame(graph.gateLoopId); graph.gateLoopId = null; }
				if (level === 'off') { graph.gateGain.gain.setTargetAtTime(1, audioCtxRef.current!.currentTime, 0.02); return; }
				const analyser = graph.analyser;
				const buf = new Float32Array(analyser.fftSize);
				const threshold = gateThresholdValue(level);
				const loop = () => {
					if (!audioCtxRef.current) return;
					if ((analyser as any).getFloatTimeDomainData) {
						(analyser as any).getFloatTimeDomainData(buf);
					} else {
						const b = new Uint8Array(buf.length); analyser.getByteTimeDomainData(b);
						for (let i = 0; i < b.length; i++) buf[i] = (b[i] - 128) / 128;
					}
					let sum = 0; for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
					const rms = Math.sqrt(sum / buf.length);
					const target = rms >= threshold ? 1 : 0;
					try { graph.gateGain.gain.setTargetAtTime(target, audioCtxRef.current.currentTime, 0.03); } catch {}
					graph.gateLoopId = requestAnimationFrame(loop);
				};
				graph.gateLoopId = requestAnimationFrame(loop);
			};

			const buildGraphForStream = async (index: 1 | 2, stream: MediaStream) => {
			const ctx = ensureAudioCtx();
			if (ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
				const source = ctx.createMediaStreamSource(stream);
				// Optional pitch shifter worklet (experimental)
				if ((index === 1 ? keyShift1 : keyShift2) !== 0) {
					try { await ctx.audioWorklet.addModule('/audio-worklets/pitch-shifter-processor.js'); } catch {}
				}
			const dryGain = ctx.createGain();
			const masterGain = ctx.createGain();
			const convolver = ctx.createConvolver();
				convolver.buffer = await ensureImpulse(ctx, index === 1 ? reverbType1 : reverbType2);
			const reverbGain = ctx.createGain();
			const delay = ctx.createDelay(1.0);
			delay.delayTime.value = 0.22; // 220 ms default echo
			const feedback = ctx.createGain();
			feedback.gain.value = 0.25;
			const echoGain = ctx.createGain();
			const eqLow = ctx.createBiquadFilter(); eqLow.type = 'lowshelf'; eqLow.frequency.value = 200;
			const eqMid = ctx.createBiquadFilter(); eqMid.type = 'peaking'; eqMid.frequency.value = 1200; eqMid.Q.value = 1.0;
			const eqHigh = ctx.createBiquadFilter(); eqHigh.type = 'highshelf'; eqHigh.frequency.value = 4000;
			const compressor = ctx.createDynamicsCompressor();
			compressor.threshold.value = -18; compressor.knee.value = 20; compressor.ratio.value = 3; compressor.attack.value = 0.003; compressor.release.value = 0.25;
				const gateGain = ctx.createGain(); gateGain.gain.value = 1;
				const analyser = ctx.createAnalyser(); analyser.fftSize = 512;
				const panner = (ctx.createStereoPanner ? ctx.createStereoPanner() : null);
			const dest = ctx.createMediaStreamDestination();

				// Chain: source -> (pitch?) -> EQ -> compressor -> analyser -> gate -> split
				let head: AudioNode = source;
				let pitchNode: AudioWorkletNode | null = null;
				const semitones = index === 1 ? keyShift1 : keyShift2;
				if (semitones !== 0 && (ctx as any).audioWorklet) {
					try {
						pitchNode = new AudioWorkletNode(ctx, 'pitch-shifter-processor', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2], parameterData: { pitchSemitones: semitones } });
						head.connect(pitchNode);
						head = pitchNode;
					} catch {}
				}
				head.connect(eqLow); eqLow.connect(eqMid); eqMid.connect(eqHigh); eqHigh.connect(compressor);
				compressor.connect(analyser);
				analyser.connect(gateGain);
			// Dry path
				gateGain.connect(dryGain);
			// Reverb path
				gateGain.connect(convolver); convolver.connect(reverbGain);
			// Echo path with feedback loop
				const delayInput = ctx.createGain();
				gateGain.connect(delayInput); delayInput.connect(delay); delay.connect(feedback); feedback.connect(delay); // feedback loop
			delay.connect(echoGain);
			// Sum to master
			dryGain.connect(masterGain);
			reverbGain.connect(masterGain);
			echoGain.connect(masterGain);
				if (panner) masterGain.connect(panner);
					// To output stream: either direct to speakers (low latency) or via MediaStream to an <audio> sink (enables setSinkId)
						if (lowLatencyMode) {
							(panner || masterGain).connect(ctx.destination);
						} else {
							(panner || masterGain).connect(dest);
						}

				const graph: MicGraph = { source, pitchNode, dryGain, masterGain, reverb: convolver, reverbGain, delay, feedback, echoGain, eqLow, eqMid, eqHigh, compressor, gateGain, analyser, gateLoopId: null, panner, dest };
			if (index === 1) micGraph1Ref.current = graph; else micGraph2Ref.current = graph;

			// Initialize levels based on current state
				const reverbVal = index === 1 ? reverb1 : reverb2; graph.reverbGain.gain.value = (reverbVal || 0) / 100;
				const echoVal = index === 1 ? echo1 : echo2; graph.echoGain.gain.value = (echoVal || 0) / 100; graph.feedback.gain.value = Math.min(0.8, (echoVal || 0) / 200);
				const lg = index === 1 ? low1 : low2; const mg = index === 1 ? mid1 : mid2; const hg = index === 1 ? high1 : high2; applyEqGains(graph, lg, mg, hg);
				const pan = index === 1 ? pan1 : pan2; if (graph.panner) graph.panner.pan.value = Math.max(-1, Math.min(1, pan));
				updateGateLoop(graph, index === 1 ? gate1 : gate2);
				// Apply initial output gain based on master and per-mic volume
				try { graph.masterGain.gain.value = (internalMasterVolume / 100) * ((index === 1 ? micVol1 : micVol2) / 100); } catch {}

					// Attach to hidden audio element with sink control (disabled in low latency mode)
					if (!lowLatencyMode) {
						const el = getMicSinkEl(index);
						el.srcObject = dest.stream;
						el.muted = false;
						try { el.volume = 1.0; } catch {}
						try { await el.play(); } catch {}
					}
		};

	const refreshDevices = async () => {
		setIsScanning(true);
		try {
			// Request permission to get labels
			try { await navigator.mediaDevices.getUserMedia({ audio: true }); } catch {}
			const devices = await navigator.mediaDevices.enumerateDevices();
			const outputs = devices.filter(d => d.kind === 'audiooutput');
			const inputs = devices.filter(d => d.kind === 'audioinput');
			setAudioDevices(outputs);
			setMicDevices(inputs);

			// Hydrate persisted mics
			try {
				const p1 = localStorage.getItem('qroke:selectedMicDevice1') || localStorage.getItem('qroke:selectedMicDevice') || '';
				const p2 = localStorage.getItem('qroke:selectedMicDevice2') || '';
				if (p1 && inputs.some(d => d.deviceId === p1)) setSelectedMic1(p1); else if (!selectedMic1 && inputs[0]) setSelectedMic1(inputs[0].deviceId);
				// choose different second mic if possible
				if (p2 && inputs.some(d => d.deviceId === p2)) setSelectedMic2(p2);
				else if (!selectedMic2) {
					const alt = inputs.find(d => d.deviceId !== (p1 || selectedMic1));
					if (alt) setSelectedMic2(alt.deviceId);
				}
			} catch {}
		} catch (e) {
			console.warn('Failed to enumerate devices', e);
		} finally {
			setIsScanning(false);
		}
	};

	const handleAudioDeviceChange = async (deviceId: string) => {
		setDeviceSelection(deviceId);
		try {
			const els = document.querySelectorAll('audio, video');
			els.forEach((el: any) => {
				if (typeof el.setSinkId === 'function') {
					el.setSinkId(deviceId).catch(() => {});
				}
			});
		} catch {}
	};

	const handleMicDeviceChange = (index: 1 | 2, deviceId: string) => {
		if (index === 1) {
			setSelectedMic1(deviceId);
			try { localStorage.setItem('qroke:selectedMicDevice1', deviceId); localStorage.setItem('qroke:selectedMicDevice', deviceId); } catch {}
		} else {
			setSelectedMic2(deviceId);
			try { localStorage.setItem('qroke:selectedMicDevice2', deviceId); } catch {}
		}
	};

		const startMicLive = async (index: 1 | 2) => {
		const deviceId = index === 1 ? selectedMic1 : selectedMic2;
		if (!deviceId) return;
		// prevent duplicate rapid starts
		if ((index === 1 && isStartingMic1) || (index === 2 && isStartingMic2)) return;
		try {
			if (index === 1) setIsStartingMic1(true); else setIsStartingMic2(true);
				const constraints: MediaStreamConstraints = {
					audio: {
						deviceId: { exact: deviceId } as any,
						echoCancellation: !lowLatencyMode,
						noiseSuppression: !lowLatencyMode,
						autoGainControl: !lowLatencyMode
					},
					video: false
				} as any;
			const s = await navigator.mediaDevices.getUserMedia(constraints);
			if (!s || s.getAudioTracks().length === 0) throw new Error('No audio tracks from selected microphone');
				// Build effect graph and route to global sink
				await buildGraphForStream(index, s);
				if (index === 1) { setMicStream1(s); setMic1Live(true); } else { setMicStream2(s); setMic2Live(true); }
		} catch (e) {
			console.error('Failed to start live mic', e);
			alert('Failed to access the selected microphone. Try reselecting a different input or checking OS privacy settings.');
		} finally {
			if (index === 1) setIsStartingMic1(false); else setIsStartingMic2(false);
		}
	};

	const stopMicLive = (index: 1 | 2) => {
		const s = index === 1 ? micStream1 : micStream2;
		if (s) { try { s.getTracks().forEach(t => t.stop()); } catch {} }
		if (index === 1) { setMicStream1(null); setMic1Live(false); } else { setMicStream2(null); setMic2Live(false); }
			const el = getMicSinkEl(index);
			if (el) { try { (el as any).srcObject = null; el.pause(); } catch {} }
			// Disconnect graph
			const graph = index === 1 ? micGraph1Ref.current : micGraph2Ref.current;
			if (graph) {
				try {
					graph.masterGain.disconnect();
					graph.reverb.disconnect();
					graph.delay.disconnect();
					graph.feedback.disconnect();
					graph.source.disconnect();
				} catch {}
				if (index === 1) micGraph1Ref.current = null; else micGraph2Ref.current = null;
			}
	};

	const handleMasterVolumeChangeInternal = (value: number) => {
		setInternalMasterVolume(value);
		// Skip mic sinks; they stay at unity. Adjust others normally.
		const els = document.querySelectorAll('audio, video');
		els.forEach((el: any) => {
			if (!el || typeof el.volume === 'undefined') return;
			const id: string = el.id || '';
			if (id === 'qroke-host-mic-1' || id === 'qroke-host-mic-2') return;
			try { el.volume = value / 100; } catch {}
		});
		// Update graph output gains as master changes
		const g1 = micGraph1Ref.current; if (g1 && g1.masterGain) { try { g1.masterGain.gain.value = (value / 100) * (micVol1 / 100); } catch {} }
		const g2 = micGraph2Ref.current; if (g2 && g2.masterGain) { try { g2.masterGain.gain.value = (value / 100) * (micVol2 / 100); } catch {} }
	};

		// React to effects changes live when mics are active
		useEffect(() => {
			const g = micGraph1Ref.current; if (!g) return;
				g.reverbGain.gain.value = (reverb1 || 0) / 100;
		}, [reverb1]);
			useEffect(() => {
				const g = micGraph1Ref.current; if (!g || !audioCtxRef.current) return;
				ensureImpulse(audioCtxRef.current, reverbType1).then((buf) => { if (g.reverb) g.reverb.buffer = buf; }).catch(() => {});
			}, [reverbType1]);
		useEffect(() => {
			const g = micGraph1Ref.current; if (!g) return;
			const amt = (echo1 || 0) / 100;
			g.echoGain.gain.value = amt;
			g.feedback.gain.value = Math.min(0.8, amt / 2);
		}, [echo1]);
			useEffect(() => {
				const g = micGraph1Ref.current; if (!g) return;
				applyEqGains(g, low1, mid1, high1);
			}, [low1, mid1, high1]);
			useEffect(() => {
				const g = micGraph1Ref.current; if (!g || !g.panner) return;
				g.panner.pan.value = Math.max(-1, Math.min(1, pan1));
			}, [pan1]);
			useEffect(() => {
				const g = micGraph1Ref.current; if (!g) return;
				updateGateLoop(g, gate1);
			}, [gate1]);
			useEffect(() => {
				const g = micGraph1Ref.current; if (!g || !audioCtxRef.current) return;
				// Rebuild pitch node only when live; for now, require toggling live off/on to apply without artifacts.
				if (mic1Live) {
					console.warn('Key change will apply on next Go Live for Mic 1');
				}
			}, [keyShift1, mic1Live]);
		useEffect(() => {
			const g = micGraph2Ref.current; if (!g) return;
				g.reverbGain.gain.value = (reverb2 || 0) / 100;
		}, [reverb2]);
			useEffect(() => {
				const g = micGraph2Ref.current; if (!g || !audioCtxRef.current) return;
				ensureImpulse(audioCtxRef.current, reverbType2).then((buf) => { if (g.reverb) g.reverb.buffer = buf; }).catch(() => {});
			}, [reverbType2]);
		useEffect(() => {
			const g = micGraph2Ref.current; if (!g) return;
			const amt = (echo2 || 0) / 100;
			g.echoGain.gain.value = amt;
			g.feedback.gain.value = Math.min(0.8, amt / 2);
		}, [echo2]);
			useEffect(() => {
				const g = micGraph2Ref.current; if (!g) return;
				applyEqGains(g, low2, mid2, high2);
			}, [low2, mid2, high2]);
			useEffect(() => {
				const g = micGraph2Ref.current; if (!g || !g.panner) return;
				g.panner.pan.value = Math.max(-1, Math.min(1, pan2));
			}, [pan2]);
			useEffect(() => {
				const g = micGraph2Ref.current; if (!g) return;
				updateGateLoop(g, gate2);
			}, [gate2]);
			useEffect(() => {
				const g = micGraph2Ref.current; if (!g || !audioCtxRef.current) return;
				if (mic2Live) {
					console.warn('Key change will apply on next Go Live for Mic 2');
				}
			}, [keyShift2, mic2Live]);

	const handleEndParty = async () => {
		if (!resolvedPartyId) return;
		setEndingParty(true);
		try {
			await endPartyClient(resolvedPartyId);
			window.location.href = '/';
		} catch (e) {
			alert('Failed to end party.');
		} finally {
			setEndingParty(false);
		}
	};

	// Queue actions
	const handleRemoveSong = async (songId: string) => {
		if (!resolvedPartyId || processingQueue) return;
		try {
			setProcessingQueue(true);
			const partyFunction = httpsCallable(functions, 'party');
			await partyFunction({ action: 'queue', partyId: resolvedPartyId, queueAction: 'remove', songId });
		} catch {
			alert('Failed to remove song');
		} finally { setProcessingQueue(false); }
	};
	const handleClearQueue = async () => {
		if (!resolvedPartyId || processingQueue) return;
		if (!confirm('Are you sure you want to clear the entire queue?')) return;
		try {
			setProcessingQueue(true);
			const partyFunction = httpsCallable(functions, 'party');
			await partyFunction({ action: 'queue', partyId: resolvedPartyId, queueAction: 'clear' });
		} catch { alert('Failed to clear queue'); }
		finally { setProcessingQueue(false); }
	};
	const handleShuffleQueue = async () => {
		if (!resolvedPartyId || processingQueue) return;
		try {
			setProcessingQueue(true);
			const partyFunction = httpsCallable(functions, 'party');
			await partyFunction({ action: 'queue', partyId: resolvedPartyId, queueAction: 'shuffle' });
		} catch { alert('Failed to shuffle queue'); }
		finally { setProcessingQueue(false); }
	};
	const handleSkipSong = async () => {
		if (!resolvedPartyId || processingQueue) return;
		try {
			setProcessingQueue(true);
			const partyFunction = httpsCallable(functions, 'party');
			await partyFunction({ action: 'next', partyId: resolvedPartyId });
		} catch { alert('Failed to skip song'); }
		finally { setProcessingQueue(false); }
	};
	const handleMoveSong = async (songId: string, direction: 'up' | 'down') => {
		if (!resolvedPartyId || processingQueue) return;
		try {
			setProcessingQueue(true);
			// Calculate new index on client and send desired position
			const idx = songQueue.findIndex((s: any) => s.id === songId);
			if (idx < 0) return;
			const newPosition = direction === 'up' ? Math.max(0, idx - 1) : Math.min(songQueue.length - 1, idx + 1);
			const partyFunction = httpsCallable(functions, 'party');
			await partyFunction({ action: 'queue', partyId: resolvedPartyId, queueAction: 'move', songId, newPosition });
		} catch { alert('Failed to move song'); }
		finally { setProcessingQueue(false); }
	};

	// Venue ads scheduling
	const scheduleVenueAdNextBreak = async () => {
		if (!resolvedPartyId) return;
		try {
			const ref = doc(db, 'parties', resolvedPartyId, 'control', 'scheduledAd');
			await setDoc(ref, { type: 'venue', status: 'scheduled', requestedAt: serverTimestamp(), durationSeconds: 25 }, { merge: true });
			alert('Venue ad scheduled for the next song break.');
		} catch { alert('Failed to schedule venue ad.'); }
	};
	const cancelScheduledVenueAd = async () => {
		if (!resolvedPartyId) return;
		try {
			const ref = doc(db, 'parties', resolvedPartyId, 'control', 'scheduledAd');
			await setDoc(ref, { status: 'cancelled', cancelledAt: serverTimestamp() }, { merge: true });
			alert('Scheduled venue ad cancelled.');
		} catch { alert('Failed to cancel venue ad.'); }
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
			<div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto text-white">
				<div className="flex justify-between items-center p-6 border-b border-gray-700">
					<h2 className="text-3xl font-bold">🎛️ Host Controls</h2>
					<button
						onClick={() => { try { onCloseWithMicStatus && onCloseWithMicStatus(mic1Live || mic2Live); } catch {}; onClose(); }}
						className="text-gray-400 hover:text-white text-3xl font-bold"
					>
						×
					</button>
				</div>

				{/* Tabs */}
						<div className="flex border-b border-gray-700">
					<button onClick={() => setActiveTab('audio')} className={`px-6 py-4 font-semibold transition-colors ${activeTab === 'audio' ? 'bg-qr-primary text-white border-b-2 border-qr-primary' : 'text-gray-400 hover:text-white'}`}>🔊 Audio</button>
							<button onClick={() => setActiveTab('sources')} className={`px-6 py-4 font-semibold transition-colors ${activeTab === 'sources' ? 'bg-qr-primary text-white border-b-2 border-qr-primary' : 'text-gray-400 hover:text-white'}`}>📦 Sources</button>
					<button onClick={() => setActiveTab('video')} className={`px-6 py-4 font-semibold transition-colors ${activeTab === 'video' ? 'bg-qr-primary text-white border-b-2 border-qr-primary' : 'text-gray-400 hover:text-white'}`}>📺 External Display</button>
					<button onClick={() => setActiveTab('queue')} className={`px-6 py-4 font-semibold transition-colors ${activeTab === 'queue' ? 'bg-qr-primary text-white border-b-2 border-qr-primary' : 'text-gray-400 hover:text-white'}`}>📋 Queue Management</button>
				</div>

				<div className="p-6">
					{/* Audio Tab */}
					{activeTab === 'audio' && (
						<div className="space-y-6">
										{/* First-time setup tip */}
										{showSetupTip && (
											<div className="bg-blue-900/40 border border-blue-700 rounded-lg p-4 flex items-start gap-3">
												<div className="text-2xl">💡</div>
												<div className="flex-1 text-sm">
													<div className="font-semibold mb-1">Quick mic setup</div>
													<ul className="list-disc pl-5 space-y-1 text-blue-100">
														<li>Choose a microphone below and click <b>Go Live</b>.</li>
														<li>Adjust volume, pan, and effects as needed.</li>
														<li>If you’ll show lyrics on another screen, open <b>External Display</b> in the next tab.</li>
													</ul>
													<label className="mt-3 inline-flex items-center gap-2 text-blue-200">
														<input
															 type="checkbox"
															 onChange={(e) => {
															   try { localStorage.setItem('qroke:coach:disableMicSetup', e.target.checked ? '1' : '0'); } catch {}
															   if (e.target.checked && onDismissSetupTip) onDismissSetupTip();
															 }}
														/>
														Don’t show this again
													</label>
												</div>
												<button onClick={onDismissSetupTip} className="text-blue-200 hover:text-white text-xl leading-none">×</button>
											</div>
										)}
							{/* Master Volume */}
							<div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-4">
								<div className="flex items-center justify-between">
									<h3 className="text-lg font-semibold text-white">Master Output</h3>
									<span className="text-sm text-gray-300">{internalMasterVolume}%</span>
								</div>
								<div className="flex items-center space-x-4">
									<span className="text-gray-400">🔈</span>
									<input type="range" min={0} max={100} value={internalMasterVolume} onChange={(e) => handleMasterVolumeChangeInternal(Number(e.target.value))} className="flex-1 accent-emerald-400" />
									<span className="text-gray-400">🔊</span>
								</div>
											<label className="mt-2 inline-flex items-center gap-2 text-sm text-white">
												<input
													type="checkbox"
													checked={lowLatencyMode}
													onChange={(e) => {
														const next = e.target.checked; setLowLatencyMode(next); try { localStorage.setItem('qroke:lowLatency', next ? '1' : '0'); } catch {}
														// If any mic is live, restart with new routing/constraints
														const anyLive = mic1Live || mic2Live;
														if (anyLive) {
															if (!confirm('Switching latency mode will restart live microphones. Continue?')) { setLowLatencyMode(!next); return; }
															if (mic1Live) stopMicLive(1); if (mic2Live) stopMicLive(2);
															// Brief delay to release old graphs, then restart
															setTimeout(() => { if (micStream1) void startMicLive(1); if (micStream2) void startMicLive(2); }, 300);
														}
													}}
												/>
												Low Latency Mode (routes mic directly to system output; disables device picker & browser DSP)
											</label>
											<div className="mt-2 text-xs bg-yellow-900/30 border border-yellow-700/50 rounded px-3 py-2 text-yellow-200">
												<strong>⚠️ Latency tip:</strong> Even with Low Latency Mode enabled, effects add delay (~5-15ms per effect). For zero-latency monitoring:
												<ul className="list-disc pl-5 mt-1 space-y-1">
													<li>Set <b>Reverb</b> and <b>Echo</b> to <b>0</b></li>
													<li>Keep <b>Key shift</b> at <b>0 st</b> (pitch shift adds ~20-40ms)</li>
													<li>Use <b>Gate: Off</b> (analysis loop adds ~3-5ms)</li>
													<li>EQ/Pan/Compressor add minimal delay (~2-5ms total)</li>
												</ul>
												Total effect latency with all enabled: <b>~50-100ms</b>. With all off: <b>~5-10ms</b>.
											</div>
							</div>

							{/* Dual host microphones */}
											<div className="mt-2 bg-gray-700/60 border border-gray-600 rounded-lg p-4 space-y-5 relative">
												<div className="flex items-center justify-between">
													<h4 className="font-semibold">Host Microphones</h4>
													<button
														aria-label="Mic options help"
														title="What do these controls do?"
														onClick={(e) => { e.stopPropagation(); setShowMicHelp((v) => !v); }}
														className="text-xs px-2 py-1 rounded-full bg-gray-600 hover:bg-gray-500 text-white font-semibold"
													>
														?
													</button>
												</div>
												<p className="text-sm text-gray-300">Select up to two local USB mics. Toggle Live to speak or sing.</p>
												{showMicHelp && (
													<div className="absolute right-3 top-12 z-20 w-80 bg-black/80 border border-gray-600 rounded-lg p-3 shadow-xl">
														<div className="text-sm font-semibold mb-2">Mic options</div>
														<ul className="text-[13px] space-y-1 text-gray-200 list-disc pl-4">
															<li><b>Vol</b>: Mic loudness to speakers.</li>
															<li><b>Pan</b>: Place voice left/right in stereo (-1 Left, 0 Center, +1 Right).</li>
															<li><b>Reverb</b>: Adds room ambience. <i>Room</i> = subtle, <i>Hall</i> = larger/longer.</li>
															<li><b>Echo</b>: Single repeat with feedback for classic karaoke echo.</li>
															<li><b>EQ</b>: Tone control. Low = warmth (bass), Mid = presence/clarity, High = sparkle. ±6 dB.</li>
															<li><b>Gate</b>: Reduces background noise when you’re not singing (Off/Low/Med/High).</li>
															<li><b>Key</b>: Shifts mic pitch ±3 semitones. Change applies on next Go Live.</li>
														</ul>
														<div className="text-[11px] text-gray-400 mt-2">Tip: Start with Room reverb 10–20, EQ at 0 dB, and adjust pan for duets.</div>
													</div>
												)}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{/* Mic 1 */}
									<div className="bg-black/30 rounded p-3 border border-gray-600">
										<div className="flex items-center justify-between gap-2 mb-2">
											<label className="text-sm">Mic 1</label>
											<select value={selectedMic1} onChange={(e) => handleMicDeviceChange(1, e.target.value)} className="bg-black/60 border border-gray-500 rounded px-2 py-1 text-white flex-1">
												<option value="">Default</option>
												{micDevices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Mic Input'}</option>)}
											</select>
										</div>
										<div className="flex items-center justify-between gap-2">
											<button className={`px-3 py-1 rounded ${mic1Live ? 'bg-red-600' : 'bg-green-600'} text-white`} disabled={!mic1Live && isStartingMic1} onClick={() => mic1Live ? stopMicLive(1) : startMicLive(1)}>
												{mic1Live ? 'Mute' : 'Go Live'}
											</button>
											<div className="flex items-center gap-2 text-sm">
												<span>Vol</span>
												<input type="range" min={0} max={100} value={micVol1} onChange={(e) => setMicVol1(Number(e.target.value))} className="w-28" />
												<span className="w-8 text-right">{micVol1}</span>
											</div>
										</div>
										<div className="mt-2"><AudioLevelMeter stream={micStream1 || null} label="Mic 1" /></div>
															{/* Simple Effects */}
															<div className="mt-3 space-y-2">
																<label className="flex items-center gap-2 text-sm">
																	Pan
																	<input type="range" min={-1} max={1} step={0.1} value={pan1} onChange={(e) => setPan1(Number(e.target.value))} />
																	<span className="w-10 text-right">{pan1.toFixed(1)}</span>
																</label>
																<label className="flex items-center gap-2 text-sm">
																	Reverb
																	<input type="range" min={0} max={100} value={reverb1} onChange={(e) => setReverb1(Number(e.target.value))} />
																	<span className="w-8 text-right">{reverb1}</span>
																</label>
																<label className="flex items-center gap-2 text-sm">
																	Type
																	<select className="bg-black/60 border border-gray-500 rounded px-2 py-1 text-white" value={reverbType1} onChange={(e) => setReverbType1(e.target.value as any)}>
																		<option value="room">Room</option>
																		<option value="hall">Hall</option>
																	</select>
																</label>
																<label className="flex items-center gap-2 text-sm">
																	Echo
																	<input type="range" min={0} max={100} value={echo1} onChange={(e) => setEcho1(Number(e.target.value))} />
																	<span className="w-8 text-right">{echo1}</span>
																</label>
																{/* 3-band EQ (improved layout) */}
																<div className="mt-1">
																	<div className="text-xs font-semibold text-gray-200 mb-1">EQ</div>
																	<div className="grid grid-cols-3 gap-3 text-sm">
																		<div>
																			<div className="flex items-center justify-between text-[12px] mb-1"><span>Low</span><span className="text-gray-300">{low1} dB</span></div>
																			<input className="w-full" type="range" min={-6} max={6} step={0.5} value={low1} onChange={(e) => setLow1(Number(e.target.value))} />
																		</div>
																		<div>
																			<div className="flex items-center justify-between text-[12px] mb-1"><span>Mid</span><span className="text-gray-300">{mid1} dB</span></div>
																			<input className="w-full" type="range" min={-6} max={6} step={0.5} value={mid1} onChange={(e) => setMid1(Number(e.target.value))} />
																		</div>
																		<div>
																			<div className="flex items-center justify-between text-[12px] mb-1"><span>High</span><span className="text-gray-300">{high1} dB</span></div>
																			<input className="w-full" type="range" min={-6} max={6} step={0.5} value={high1} onChange={(e) => setHigh1(Number(e.target.value))} />
																		</div>
																	</div>
																</div>
																{/* Gate */}
																<label className="flex items-center gap-2 text-sm">Gate
																	<select className="bg-black/60 border border-gray-500 rounded px-2 py-1 text-white" value={gate1} onChange={(e) => setGate1(e.target.value as any)}>
																		<option value="off">Off</option>
																		<option value="low">Low</option>
																		<option value="med">Med</option>
																		<option value="high">High</option>
																	</select>
																</label>
																{/* Key (Pitch) */}
																<label className="flex items-center gap-2 text-sm">Key
																	<input type="range" min={-3} max={3} step={1} value={keyShift1} onChange={(e) => setKeyShift1(Number(e.target.value))} />
																	<span className="w-10 text-right">{keyShift1} st</span>
																</label>
															</div>
									</div>
									{/* Mic 2 */}
									<div className="bg-black/30 rounded p-3 border border-gray-600">
										<div className="flex items-center justify-between gap-2 mb-2">
											<label className="text-sm">Mic 2</label>
											<select value={selectedMic2} onChange={(e) => handleMicDeviceChange(2, e.target.value)} className="bg-black/60 border border-gray-500 rounded px-2 py-1 text-white flex-1">
												<option value="">Default</option>
												{micDevices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Mic Input'}</option>)}
											</select>
										</div>
										<div className="flex items-center justify-between gap-2">
											<button className={`px-3 py-1 rounded ${mic2Live ? 'bg-red-600' : 'bg-green-600'} text-white`} disabled={!mic2Live && isStartingMic2} onClick={() => mic2Live ? stopMicLive(2) : startMicLive(2)}>
												{mic2Live ? 'Mute' : 'Go Live'}
											</button>
											<div className="flex items-center gap-2 text-sm">
												<span>Vol</span>
												<input type="range" min={0} max={100} value={micVol2} onChange={(e) => setMicVol2(Number(e.target.value))} className="w-28" />
												<span className="w-8 text-right">{micVol2}</span>
											</div>
										</div>
										<div className="mt-2"><AudioLevelMeter stream={micStream2 || null} label="Mic 2" /></div>
															{/* Simple Effects */}
															<div className="mt-3 space-y-2">
																<label className="flex items-center gap-2 text-sm">
																	Pan
																	<input type="range" min={-1} max={1} step={0.1} value={pan2} onChange={(e) => setPan2(Number(e.target.value))} />
																	<span className="w-10 text-right">{pan2.toFixed(1)}</span>
																</label>
																<label className="flex items-center gap-2 text-sm">
																	Reverb
																	<input type="range" min={0} max={100} value={reverb2} onChange={(e) => setReverb2(Number(e.target.value))} />
																	<span className="w-8 text-right">{reverb2}</span>
																</label>
																<label className="flex items-center gap-2 text-sm">
																	Type
																	<select className="bg-black/60 border border-gray-500 rounded px-2 py-1 text-white" value={reverbType2} onChange={(e) => setReverbType2(e.target.value as any)}>
																		<option value="room">Room</option>
																		<option value="hall">Hall</option>
																	</select>
																</label>
																<label className="flex items-center gap-2 text-sm">
																	Echo
																	<input type="range" min={0} max={100} value={echo2} onChange={(e) => setEcho2(Number(e.target.value))} />
																	<span className="w-8 text-right">{echo2}</span>
																</label>
																{/* 3-band EQ (improved layout) */}
																<div className="mt-1">
																	<div className="text-xs font-semibold text-gray-200 mb-1">EQ</div>
																	<div className="grid grid-cols-3 gap-3 text-sm">
																		<div>
																			<div className="flex items-center justify-between text-[12px] mb-1"><span>Low</span><span className="text-gray-300">{low2} dB</span></div>
																			<input className="w-full" type="range" min={-6} max={6} step={0.5} value={low2} onChange={(e) => setLow2(Number(e.target.value))} />
																		</div>
																		<div>
																			<div className="flex items-center justify-between text-[12px] mb-1"><span>Mid</span><span className="text-gray-300">{mid2} dB</span></div>
																			<input className="w-full" type="range" min={-6} max={6} step={0.5} value={mid2} onChange={(e) => setMid2(Number(e.target.value))} />
																		</div>
																		<div>
																			<div className="flex items-center justify-between text-[12px] mb-1"><span>High</span><span className="text-gray-300">{high2} dB</span></div>
																			<input className="w-full" type="range" min={-6} max={6} step={0.5} value={high2} onChange={(e) => setHigh2(Number(e.target.value))} />
																		</div>
																	</div>
																</div>
																{/* Gate */}
																<label className="flex items-center gap-2 text-sm">Gate
																	<select className="bg-black/60 border border-gray-500 rounded px-2 py-1 text-white" value={gate2} onChange={(e) => setGate2(e.target.value as any)}>
																		<option value="off">Off</option>
																		<option value="low">Low</option>
																		<option value="med">Med</option>
																		<option value="high">High</option>
																	</select>
																</label>
																{/* Key (Pitch) */}
																<label className="flex items-center gap-2 text-sm">Key
																	<input type="range" min={-3} max={3} step={1} value={keyShift2} onChange={(e) => setKeyShift2(Number(e.target.value))} />
																	<span className="w-10 text-right">{keyShift2} st</span>
																</label>
															</div>
									</div>
								</div>
								<p className="text-[11px] text-gray-400 mt-2">Tip: To avoid feedback, keep speakers in front of the mics and reduce volume.</p>
							</div>

							{/* Audio Output + Test Tone */}
							<div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-4">
								<div className="flex items-center justify-between">
									<h3 className="text-lg font-semibold text-white">Audio Output Device</h3>
									<button onClick={refreshDevices} disabled={isScanning} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-4 py-2 rounded-lg font-medium">{isScanning ? '🔍 Scanning...' : '🔍 Scan Devices'}</button>
								</div>
								<select value={deviceSelection} onChange={(e) => handleAudioDeviceChange(e.target.value)} className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600">
									<option value="">Default Audio Device</option>
									{audioDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Audio Output'}</option>)}
								</select>
								<TestTonePanel devices={audioDevices} selectedDeviceId={deviceSelection} onDeviceChange={(id) => void handleAudioDeviceChange(id)} />
							</div>
						</div>
					)}

								{/* Sources Tab */}
								{activeTab === 'sources' && (
									<div className="space-y-6">
										<div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-3">
											<h3 className="text-lg font-semibold text-white">Local Library</h3>
											<p className="text-sm text-gray-300">Connect a folder with your licensed karaoke tracks. Files never leave this device. Only metadata is published to guests.</p>
											<div className="flex items-center gap-3 flex-wrap">
												{/* Hidden file input for Firefox (fallback when showDirectoryPicker is unavailable) */}
												<input
													type="file"
													multiple
													accept=".mp3,.ogg,.wav,.m4a,.mp4,.webm,.cdg"
													className="hidden"
													ref={(el) => (window as any).__qrokeFilePicker = el}
													onChange={async (e) => {
														try {
															const files = Array.from(e.target?.files || []);
															if (files.length === 0) return;
															setIsIndexing(true);
															const allowed = ['.mp3','.ogg','.wav','.m4a','.mp4','.webm','.cdg'];
															type Scanned = { rel: string; ext: string; base: string; dir: string; handle: any };
															const scanned: Scanned[] = [];
															const libMap = new Map<string, any>();
															const toHash = (s: string) => { let h=0; for (let i=0;i<s.length;i++){ h=((h<<5)-h)+s.charCodeAt(i); h|=0; } return 'loc_'+Math.abs(h).toString(36); };
															const parseMeta = (name: string) => {
																const base = name.replace(/\.[^.]+$/, '');
																const m = base.match(/^\s*([^\-]+?)\s*-\s*(.+)$/);
																if (m) return { artist: m[1].trim() || null, title: m[2].trim() || base };
																return { artist: null, title: base };
															};
															// Wrap File into a faux FileSystemFileHandle-like object
															const wrapFile = (f: File) => ({
																kind: 'file',
																name: f.name,
																getFile: async () => f,
															});
															for (const f of files) {
																const lower = f.name.toLowerCase();
																const ext = lower.substring(lower.lastIndexOf('.'));
																if (!allowed.includes(ext)) continue;
																const rel = f.webkitRelativePath || f.name; // Firefox won't have webkitRelativePath
																const base = lower.replace(/\.[^.]+$/, '');
																const dir = rel.includes('/') ? rel.substring(0, rel.lastIndexOf('/')) : '';
																scanned.push({ rel, ext, base, dir, handle: wrapFile(f) });
															}
															// Build audio+cdg pairs
															const byKey = new Map<string, { audio?: Scanned; cdg?: Scanned }>();
															for (const s of scanned) {
																const key = s.dir + '::' + s.base;
																const rec = byKey.get(key) || {};
																if (s.ext === '.cdg') rec.cdg = s; else rec.audio = (rec.audio ? rec.audio : s);
																byKey.set(key, rec);
															}
															const index: LibraryItem[] = [];
															for (const [, rec] of byKey.entries()) {
																const sample = rec.audio || rec.cdg!;
																const meta = parseMeta(sample.rel.split('/').pop() || '');
																if (rec.audio && rec.cdg) {
																	const id = toHash(sample.rel + '|cdg');
																	index.push({ id, title: meta.title, artist: meta.artist, durationSeconds: null, kind: 'cdg', mp3Path: rec.audio.rel, cdgPath: rec.cdg.rel });
																	libMap.set(id, { mp3: rec.audio.handle, cdg: rec.cdg.handle });
																} else if (rec.audio) {
																	const id = toHash(rec.audio.rel);
																	const isVideo = ['.mp4','.webm'].includes(rec.audio.ext);
																	index.push({ id, title: meta.title, artist: meta.artist, durationSeconds: null, kind: isVideo ? 'video' : 'audio', path: rec.audio.rel });
																	libMap.set(id, rec.audio.handle);
																}
															}
															(window as any).qrokeLocalLibrary = libMap;
															setLibraryIndex(index);
														} catch (err) {
															console.warn('File selection cancelled or failed', err);
														} finally {
															setIsIndexing(false);
															// Reset input so selecting the same files again re-triggers change
															if ((window as any).__qrokeFilePicker) (window as any).__qrokeFilePicker.value = '';
														}
													}}
												/>
												<button
													className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white"
													onClick={async () => {
														try {
															// If File System Access API is not available (e.g., Firefox), fall back to multiple file selection
															if (!(window as any).showDirectoryPicker) {
																const input = (window as any).__qrokeFilePicker as HTMLInputElement | undefined;
																if (input) input.click();
																else console.warn('No file input available for fallback selection.');
																return;
															}
															const handle: FileSystemDirectoryHandle = await (window as any).showDirectoryPicker();
															if (!handle) return;
															setFolderHandle(handle);
															setIsIndexing(true);
															// Walk directory and index media files
																					const allowed = ['.mp3','.ogg','.wav','.m4a','.mp4','.webm','.cdg'];
																					type Scanned = { rel: string; ext: string; base: string; dir: string; handle: FileSystemFileHandle };
																					const scanned: Scanned[] = [];
																					const libMap = new Map<string, any>();
															const toHash = (s: string) => {
																let h=0; for (let i=0;i<s.length;i++){ h=((h<<5)-h)+s.charCodeAt(i); h|=0; } return 'loc_'+Math.abs(h).toString(36);
															};
															const parseMeta = (name: string) => {
																const base = name.replace(/\.[^.]+$/, '');
																// Try "Artist - Title" first
																const m = base.match(/^\s*([^\-]+?)\s*-\s*(.+)$/);
																if (m) return { artist: m[1].trim() || null, title: m[2].trim() || base };
																return { artist: null, title: base };
															};
															const walk = async (dir: FileSystemDirectoryHandle, prefix: string) => {
																// @ts-ignore - for-await is supported on iterables
																// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
																// Iterate entries
																// Using any to support different TS libdom levels
																// @ts-ignore
																for await (const entry of (dir as any).values()) {
																	if (entry.kind === 'directory') {
																		await walk(entry, prefix + '/' + entry.name);
																								} else if (entry.kind === 'file') {
																		const lower = entry.name.toLowerCase();
																		if (allowed.some(ext => lower.endsWith(ext))) {
																										const rel = (prefix ? prefix + '/' : '') + entry.name;
																										const ext = lower.substring(lower.lastIndexOf('.'));
																										const base = lower.replace(/\.[^.]+$/, '');
																										const dirName = prefix;
																										scanned.push({ rel, ext, base, dir: dirName, handle: entry });
																		}
																	}
																}
															};
															await walk(handle, '');
																						// Build pairs for MP3+CDG and classify items
																						const byKey = new Map<string, { audio?: Scanned; cdg?: Scanned }>();
																						for (const s of scanned) {
																							const key = s.dir + '::' + s.base;
																							const rec = byKey.get(key) || {};
																							if (s.ext === '.cdg') rec.cdg = s; else rec.audio = (rec.audio ? rec.audio : s);
																							byKey.set(key, rec);
																						}
																						const index: LibraryItem[] = [];
																						for (const [key, rec] of byKey.entries()) {
																							const sample = rec.audio || rec.cdg!;
																							const meta = parseMeta(sample.rel.split('/').pop() || '');
																							if (rec.audio && rec.cdg) {
																								const id = toHash(sample.rel + '|cdg');
																								index.push({ id, title: meta.title, artist: meta.artist, durationSeconds: null, kind: 'cdg', mp3Path: rec.audio.rel, cdgPath: rec.cdg.rel });
																								libMap.set(id, { mp3: rec.audio.handle, cdg: rec.cdg.handle });
																							} else if (rec.audio) {
																								const id = toHash(rec.audio.rel);
																								const isVideo = ['.mp4','.webm'].includes(rec.audio.ext);
																								index.push({ id, title: meta.title, artist: meta.artist, durationSeconds: null, kind: isVideo ? 'video' : 'audio', path: rec.audio.rel });
																								libMap.set(id, rec.audio.handle);
																							}
																						}
																						// Expose host-only library map for playback (as Map)
																						(window as any).qrokeLocalLibrary = libMap;
																						setLibraryIndex(index);
														} catch (e) {
															console.warn('Folder connect cancelled or failed', e);
														} finally {
															setIsIndexing(false);
														}
													}}
												>
													{folderHandle ? 'Reconnect Folder' : 'Connect Folder'}
												</button>
												{folderHandle && (
													<span className="text-sm text-gray-300">Connected: <b>{(folderHandle as any).name || 'Folder'}</b></span>
												)}
												<span className="text-sm text-gray-400">Indexed: {libraryIndex.length} files</span>
												{isIndexing && <span className="text-sm text-yellow-300">Indexing…</span>}
											</div>

															{/* Publish to Firestore */}
											<div className="mt-3 flex items-center gap-3">
												<button
													className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded text-white disabled:bg-gray-600"
													disabled={!resolvedPartyId || libraryIndex.length === 0}
													onClick={async () => {
														if (!resolvedPartyId) return;
														try {
															setPublishStatus('Publishing…');
															// Clear existing catalog? For now overwrite same IDs only.
															const chunkSize = 400;
															for (let i=0;i<libraryIndex.length;i+=chunkSize) {
																const batch = writeBatch(db);
																const slice = libraryIndex.slice(i, i+chunkSize);
																for (const e of slice) {
																	const ref = doc(collection(db, 'parties', resolvedPartyId, 'localCatalog'), e.id);
																					batch.set(ref, { title: e.title, artist: e.artist ?? null, durationSeconds: e.durationSeconds ?? null, hasCdg: e.kind === 'cdg' });
																}
																await batch.commit();
															}
															setPublishStatus(`Published ${libraryIndex.length} items.`);
															alert('Local catalog published for guests to browse.');
														} catch (e) {
															console.error('Publish failed', e);
															setPublishStatus('Publish failed');
															alert('Failed to publish catalog.');
														}
													}}
												>
													Publish Catalog
												</button>
												{publishStatus && <span className="text-sm text-gray-300">{publishStatus}</span>}
											</div>

											{/* Filter and preview */}
											{libraryIndex.length > 0 && (
												<div className="mt-4">
													<input
														placeholder="Filter indexed songs…"
														value={libFilter}
														onChange={(e) => setLibFilter(e.target.value)}
														className="w-full px-3 py-2 bg-black/40 border border-gray-600 rounded text-white mb-2"
													/>
													<div className="max-h-60 overflow-y-auto divide-y divide-gray-700 bg-black/30 rounded">
																				{libraryIndex
															.filter(e => {
																const q = libFilter.trim().toLowerCase();
																if (!q) return true;
																return e.title.toLowerCase().includes(q) || (e.artist?.toLowerCase().includes(q) ?? false);
															})
															.slice(0, 300)
																					.map(e => (
																						<div key={e.id} className="px-3 py-2 flex items-center justify-between">
																	<div className="min-w-0">
																		<div className="font-medium truncate">{e.title}</div>
																								<div className="text-sm text-gray-300 truncate">{e.artist || 'Unknown Artist'} {e.kind === 'cdg' ? <span className="ml-2 px-2 py-0.5 text-[10px] bg-purple-700 rounded">CD+G</span> : null}</div>
																	</div>
																	<button
																		className="ml-3 px-2 py-1 text-sm bg-gray-600 hover:bg-gray-700 rounded"
																		title="Preview on host"
																		onClick={async () => {
																									const map: Map<string, any> | undefined = (window as any).qrokeLocalLibrary;
																									if (!map || !map.has(e.id)) { alert('File handle not available. Reconnect folder.'); return; }
																									const ref = map.get(e.id);
																									try {
																										if (e.kind === 'cdg') {
																											const mp3File = await ref.mp3.getFile();
																											const cdgFile = await ref.cdg.getFile();
																											const url = URL.createObjectURL(mp3File);
																											const cdgBuf = await cdgFile.arrayBuffer();
																											const evt = new CustomEvent('qroke:localTrackSelected', { detail: { kind: 'cdg', mp3Url: url, cdgBuffer: cdgBuf, title: e.title, artist: e.artist || undefined } });
																											window.dispatchEvent(evt);
																										} else {
																											const file = await (ref as FileSystemFileHandle).getFile();
																											const url = URL.createObjectURL(file);
																											const evt = new CustomEvent('qroke:localTrackSelected', { detail: { url, name: file.name, title: e.title, artist: e.artist || undefined } });
																											window.dispatchEvent(evt);
																										}
																									} catch {
																										alert('Failed to open local file.');
																									}
																		}}
																	>Play</button>
																</div>
															))}
													</div>
												</div>
											)}
										</div>

										{/* Ad-hoc local file play */}
										<div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-3">
											<h4 className="text-white font-semibold">Ad‑hoc Local Track</h4>
											<div className="flex items-center gap-3 flex-wrap text-sm">
												<input
													type="file"
													accept="video/*,audio/*"
													className="block text-sm"
													onChange={async (e) => {
														const file = e.target.files?.[0];
														if (!file) return;
														const url = URL.createObjectURL(file);
														const evt = new CustomEvent('qroke:localTrackSelected', { detail: { url, name: file.name } });
														window.dispatchEvent(evt);
													}}
												/>
												<button
													className="px-3 py-1 bg-gray-600 hover:bg-gray-700 rounded"
													onClick={() => window.dispatchEvent(new CustomEvent('qroke:localTrackClear'))}
												>
													Stop Local Track
												</button>
											</div>
											<p className="text-[11px] text-gray-300">Plays on the host device only. Guests/external display won’t receive this media.</p>
										</div>
									</div>
								)}

								{/* Video Tab */}
					{activeTab === 'video' && (
						<div className="space-y-6">
											{/* Track source moved to Sources tab */}
							<div>
								<label className="block text-sm font-medium text-gray-300 mb-2">External Display URL:</label>
								<div className="flex items-center">
									<input type="text" value={externalDisplayUrl} readOnly className="flex-1 p-3 bg-gray-600 text-white rounded-lg border border-gray-500" />
									<button className="ml-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg" onClick={() => navigator.clipboard.writeText(externalDisplayUrl)}>📋 Copy</button>
									<button className="ml-2 px-4 py-3 bg-qr-primary hover:bg-qr-secondary text-white rounded-lg" onClick={() => { const w = window.open(externalDisplayUrl, '_blank', 'noopener'); if (w) w.focus(); }}>🖥️ Open</button>
								</div>
							</div>
							<div className="bg-gray-700/70 border border-gray-600 rounded-lg p-5 space-y-4">
								<div className="flex items-center justify-between">
									<h3 className="text-lg font-semibold text-white">Track (YouTube) Volume</h3>
									<span className="text-sm text-gray-300">{extVolume}%</span>
								</div>
								<div className="flex items-center space-x-4">
									<span className="text-gray-400">🔈</span>
									<input type="range" min={0} max={100} value={extVolume} onChange={(e) => { const next = Number(e.target.value); setExtVolume(next); try { const key = `qroke:ytv:${resolvedPartyId}:volume`; if (resolvedPartyId) localStorage.setItem(key, String(next)); } catch {} }} className="flex-1 accent-purple-400" />
									<span className="text-gray-400">🔊</span>
								</div>
								<label className="inline-flex items-center gap-2 text-sm text-white">
									<input type="checkbox" checked={extMuted} onChange={(e) => { const checked = e.target.checked; setExtMuted(checked); try { const key = `qroke:ytv:${resolvedPartyId}:muted`; if (resolvedPartyId) localStorage.setItem(key, checked ? '1' : '0'); } catch {} }} />
									Mute External Window
								</label>
								<p className="text-[11px] text-gray-400">Controls the YouTube player in the external window using same‑origin storage events.</p>
							</div>
						</div>
					)}

					{/* Queue Tab */}
					{activeTab === 'queue' && (
						<div className="space-y-6">
							<div className="flex items-center gap-3 mb-2">
								<button className="px-4 py-2 bg-green-600 text-white rounded" onClick={handleShuffleQueue} disabled={processingQueue || songQueue.length === 0}>Shuffle</button>
								<button className="px-4 py-2 bg-red-600 text-white rounded" onClick={handleClearQueue} disabled={processingQueue || songQueue.length === 0}>Clear</button>
								<button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={handleSkipSong} disabled={processingQueue}>Skip</button>

								{/* Splash controls */}
								<div className="ml-6 flex items-center gap-2 bg-gray-800/60 p-2 rounded">
									<label className="text-white text-sm font-semibold flex items-center gap-2">
										Splash:
										<input type="number" min={0} max={99} value={splashCountdown} onChange={(e) => (onSplashCountdownChange ? onSplashCountdownChange(Math.max(0, Number(e.target.value))) : setLocalSplashCountdown(Math.max(0, Number(e.target.value))))} className="w-14 text-center text-sm font-bold bg-black/60 border border-gray-500 rounded px-2 py-1 text-white" />
									</label>
									<button className={`px-3 py-1 rounded bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold ${splashPaused ? 'opacity-70' : ''}`} onClick={() => (onSplashPausedChange ? onSplashPausedChange(!splashPaused) : setLocalSplashPaused(!splashPaused))}>
										{splashPaused ? 'Resume' : 'Pause'}
									</button>
								</div>

								{/* Venue ads schedule/cancel */}
								{subscription.isActive && subscription.tier !== 'free' ? (
									<div className="ml-3 flex items-center gap-2 bg-purple-900/20 border border-purple-700/40 p-2 rounded">
										<span className="text-sm">Venue Ad:</span>
										<button onClick={scheduleVenueAdNextBreak} disabled={venueAdScheduled?.status === 'scheduled'} className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 px-3 py-1 rounded text-white text-sm">▶ Play at Next Break</button>
										<button onClick={cancelScheduledVenueAd} disabled={!venueAdScheduled || venueAdScheduled.status !== 'scheduled'} className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-600 px-3 py-1 rounded text-white text-sm">✕ Cancel</button>
									</div>
								) : (
									<div className="ml-3 flex items-center gap-2 bg-gray-800/60 border border-gray-700/60 p-2 rounded text-gray-400" title="Premium required">
										<span className="text-sm">Venue Ad:</span>
										<button disabled className="bg-gray-700 px-3 py-1 rounded text-white text-sm opacity-60 cursor-not-allowed">▶ Play at Next Break</button>
										<button disabled className="bg-gray-700 px-3 py-1 rounded text-white text-sm opacity-60 cursor-not-allowed">✕ Cancel</button>
									</div>
								)}
							</div>

							<div>
								<h3 className="font-semibold mb-2">Current Queue</h3>
								{songQueue && songQueue.length > 0 ? (
									<ul className="list-disc pl-6">
										{songQueue.map((song: any) => (
											<li key={song.id} className="mb-2 flex items-center justify-between">
												<div>
													<span className="font-semibold">{song.title}</span>
													<div className="text-gray-500 text-sm">{song.singerName || song.requestedBy || 'Unknown'}</div>
												</div>
												<div className="flex gap-2 items-center">
													<button className="px-2 py-1 bg-red-500 text-white rounded" onClick={() => void handleRemoveSong(song.id)}>Remove</button>
													<button className="px-2 py-1 bg-gray-400 text-black rounded" onClick={() => void handleMoveSong(song.id, 'up')} title="Move Up">↑</button>
													<button className="px-2 py-1 bg-gray-400 text-black rounded" onClick={() => void handleMoveSong(song.id, 'down')} title="Move Down">↓</button>
												</div>
											</li>
										))}
									</ul>
								) : (
									<div className="text-gray-500">Queue is empty.</div>
								)}
							</div>
						</div>
					)}
				</div>

				<div className="flex justify-between items-center p-6 border-t border-gray-700">
					<button onClick={handleEndParty} disabled={endingParty} className="bg-red-600 hover:bg-red-700 px-6 py-3 rounded-lg font-semibold transition-colors">{endingParty ? 'Ending Party...' : 'End Party'}</button>
					<button
						onClick={() => { try { onCloseWithMicStatus && onCloseWithMicStatus(mic1Live || mic2Live); } catch {}; onClose(); }}
						className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors"
					>
						Close
					</button>
				</div>
			</div>
		</div>
	);
};

export default HostControlsModalRegular;